"use client"

import useEmblaCarousel from "embla-carousel-react"
import React, { useCallback, useEffect, useState } from "react"

import isExternalUrl from "@/helpers/isExternalUrl"
import { AllFeaturedCardsProps } from "@/types/events"

import ArrowCarousel from "../../../../public/icons/arrow-carousel.svg"
import BackgroundImage from "../BackgroundImage"
import Button from "../Button"
import PageGrid from "../PageGrid"

const AllFeaturedCards: React.FC<AllFeaturedCardsProps> = ({
  data,
  ...sectionProps
}) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: false,
    align: "center",
    skipSnaps: false,
    dragFree: false,
    duration: 20,
    slidesToScroll: 1,
  })
  const [canScrollPrev, setCanScrollPrev] = useState(false)
  const [canScrollNext, setCanScrollNext] = useState(false)

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev()
  }, [emblaApi])

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext()
  }, [emblaApi])

  const onSelect = useCallback((api: any) => {
    setCanScrollPrev(api.canScrollPrev())
    setCanScrollNext(api.canScrollNext())
  }, [])

  useEffect(() => {
    if (!emblaApi) return

    onSelect(emblaApi)
    emblaApi.on("reInit", onSelect)
    emblaApi.on("select", onSelect)
  }, [emblaApi, onSelect])

  return (
    <section className="py-8 md:py-12 lg:py-16 xl:py-22" {...sectionProps}>
      {/* Embla viewport */}
      <div className="embla" ref={emblaRef}>
        <div className="embla__container">
          {data.map(
            ({ eventImage, title, description, buttonCta, buttonUrl }) => (
              <div key={title} className="embla__slide flex-[0_0_100%] min-w-0">
                <PageGrid>
                  <BackgroundImage
                    className="w-full h-full p-4 bg-cover rounded-lg col-span-full md:p-8 xl:p-16 lg:p-12"
                    image={eventImage.url}
                    key={title}
                  >
                    <div className="max-w-[460px] text-white-100">
                      <h6 className="mb-4 caption3 lg:caption2 xl:caption3 xl:mb-12 lg:mb-10 md:mb-8">
                        Featured
                      </h6>
                      <h4 className="mb-4 lg:mb-6 headline4 md:headline3 lg:headline2 xl:headline1">
                        {title}
                      </h4>
                      <p className="mb-10 body3 xl:body2 xl:mb-12">
                        {description}
                      </p>
                      <div className="min-h-[48px] flex items-start">
                        {buttonCta && buttonUrl && (
                          <Button
                            to={buttonUrl}
                            target={
                              isExternalUrl(buttonUrl) ? "_blank" : undefined
                            }
                          >
                            {buttonCta}
                          </Button>
                        )}
                      </div>
                    </div>
                  </BackgroundImage>
                </PageGrid>
              </div>
            ),
          )}
        </div>
      </div>

      {data.length > 1 ? (
        <PageGrid className="mt-8" isGrid={false}>
          <button
            onClick={scrollPrev}
            disabled={!canScrollPrev}
            className="disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-50 cursor-pointer relative z-10"
            type="button"
            aria-label="Previous slide"
          >
            <ArrowCarousel className="mr-10 transform -rotate-180 h-14 w-14" />
          </button>
          <button
            onClick={scrollNext}
            disabled={!canScrollNext}
            className="disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-50 cursor-pointer relative z-10"
            type="button"
            aria-label="Next slide"
          >
            <ArrowCarousel className="h-14 w-14" />
          </button>
        </PageGrid>
      ) : (
        ""
      )}
    </section>
  )
}

export default AllFeaturedCards
